import { WISHLIST_LOCAL_STORAGE_KEY, WISHLIST_ROOM_ID_QUERY_PARAM } from '@/services/wishlist';

App.namespace('es6.shared.wish_list', () => {
  const HEARTS_SELECTOR = '.js-wishlist-toggle';

  let wishListRooms = [];
  let userData = null;

  const TRACKING_ACTIONS = {
    add: 'add',
    autoAdd: 'auto-add',
    addGuest: 'add-guest',
    remove: 'remove',
  };

  const track = (action, roomId, tracking) => {
    App.track.custom(window.__DATA_TRACKING_WISHLIST_ACTIONS, {
      action,
      roomId,
      tagSlug: window.__DATA_TRACKING_VAR__TAG_SLUG,
    });

    const data = {
      element: tracking?.element ?? 'favourite_room',
      section: tracking?.section ?? 'room_card',
      room_id: roomId,
      element_value: roomId,
      country: window.__TGV_ISO_COUNTRY_CODE,
      interaction: action === 'remove' ? 'unselect' : 'select',
    };

    App.track.event('favourites_list_updated', data);
  };

  const getSavedRoomsIds = () => {
    return [...wishListRooms];
  };

  const getSavedRoomsCount = () => {
    return getSavedRoomsIds().length;
  };

  const removeAjax = (roomId) => {
    return $.post('/ajax/wish-list/remove', { roomId });
  };

  /**
   * @param search  what the caller's page shows the Client searching for, empty when it shows none
   */
  const addAjax = (roomId, search = {}) => {
    return new Promise((resolve, reject) => {
      $.post('/ajax/wish-list/add', {
        roomId,
        roomTagSlug: search.roomTagSlug,
        searchedDate: search.searchedDate,
      })
        .done((response) => {
          resolve(response);
        })
        .fail(() => {
          reject()
        })
    });
  };

  const getSavedRoomIndex = (roomId) => {
    if (wishListRooms) {
      return wishListRooms.indexOf(roomId);
    }

    return null;
  };

  const updateDOMWithState = () => {
    $(HEARTS_SELECTOR).each((index, el) => {
      const id = +el.dataset.id;

      if (wishListRooms.indexOf(id) > -1) {
        el.classList.add('c-room-card__heart--selected');
      } else {
        el.classList.remove('c-room-card__heart--selected');
      }
    });

    $.publish('wishList:change', wishListRooms.length);
  };

  const isGuest = () => {
    return userData.type === 'guest';
  };

  const redirectToLoginPage = (roomId) => {
    window.sessionStorage.setItem(WISHLIST_LOCAL_STORAGE_KEY, roomId);
    window.location = `/signup/client?redirectUrl=${window.location.href}`;
  };

  /**
   * @param index
   * @param roomId
   * @returns {boolean}  True if was successfully added
   */
  const removeFromSaved = (index, roomId, tracking) => {
    const rooms = wishListRooms;

    const updatedRooms = [
      ...rooms.slice(0, index),
      ...rooms.slice(index + 1),
    ];

    wishListRooms = updatedRooms;

    const removed = removeAjax(roomId);

    track(TRACKING_ACTIONS.remove, roomId, tracking);
    updateDOMWithState();

    // The room the message names comes back with the response, so the message waits for it rather
    // than rendering once without a thumbnail and again with one.
    removed
      .done((response) => showRemovedMessage(response?.room))
      .fail(() => showRemovedMessage(null));

    return true;
  };

  const showRemovedMessage = (room) => {
    if (typeof window.showMessageAboutSuccessfulRemovingToWishlist === 'function') {
      window.showMessageAboutSuccessfulRemovingToWishlist(room);
    }
  };

  const FLASH_MESSAGE_WAIT_INTERVAL_MS = 100;
  const FLASH_MESSAGE_WAIT_MAX_ATTEMPTS = 100;

  // The flash message lives in a Vue component that mounts only once the enquiry form has loaded,
  // which can be after an auto-save that follows a login; wait for it rather than drop the message.
  const showMessageAboutSuccessfulAdding = (room, attempt = 0) => {
    if (typeof window.showMessageAboutSuccessfulAddingToWishlist === 'function') {
      window.showMessageAboutSuccessfulAddingToWishlist(room);
      return;
    }

    if (attempt < FLASH_MESSAGE_WAIT_MAX_ATTEMPTS) {
      setTimeout(() => showMessageAboutSuccessfulAdding(room, attempt + 1), FLASH_MESSAGE_WAIT_INTERVAL_MS);
    }
  };

  /**
   * @param roomId
   * @returns {Promise{boolean}} True if was successfully added
   */
  const addToSaved =(roomId, { autoSaved } = {}, tracking = {}, search = {}) => {
    return new Promise((resolve, reject) => {
      if (isGuest()) {
        track(TRACKING_ACTIONS.addGuest, roomId, tracking);

        if (typeof window.triggerWishlistModal === 'function') {
          window.triggerWishlistModal(roomId);
          return resolve(false);
        }

        setTimeout(() => redirectToLoginPage(roomId), 100);
        return resolve(false);
      }

      addAjax(roomId, search)
        .then((response) => {
          window.sessionStorage.removeItem(WISHLIST_LOCAL_STORAGE_KEY);

          if (wishListRooms.includes(roomId)) {
            // Logging in to save a space that was already saved is still a save the Client asked for,
            // and the message is the only sign on the page that it worked.
            if (autoSaved) {
              showMessageAboutSuccessfulAdding(response?.room ?? null);
            }

            return resolve(false);
          }

          wishListRooms = [...wishListRooms, roomId];
          track(autoSaved ? TRACKING_ACTIONS.autoAdd : TRACKING_ACTIONS.add, roomId, tracking);

          showMessageAboutSuccessfulAdding(response?.room ?? null);

          updateDOMWithState();

          return resolve(true);
        })
        .catch((error) => {
          reject(false);
          throw error;
        });
    });
  };

  const toggleSaved = (roomId, tracking, search = {}) => {
    const roomIndex = getSavedRoomIndex(roomId);
    if (roomIndex > -1) {
      window.sessionStorage.removeItem(WISHLIST_LOCAL_STORAGE_KEY);
      return Promise.resolve(removeFromSaved(roomIndex, roomId, tracking));
    } else {
      return addToSaved(roomId, {}, tracking, search);
    }
  };

  const isFavourite = (roomId) => {
    const index = getSavedRoomIndex(roomId);

    if (index > -1) {
      return true;
    }

    return false;
  };

  const updateRoomsFromUrl = () => {
    if (isGuest()) {
      return;
    }

    const roomId = window.sessionStorage.getItem(WISHLIST_LOCAL_STORAGE_KEY);
    if (roomId) {
      addToSaved(+roomId, { autoSaved: true }).then(() => {
        $.publish('wishList:added_from_url', +roomId);
        updateDOMWithState();
      });
    }
  };

  // Session storage belongs to the tab that opened the login modal, but a magic link lands in whichever
  // tab opens the email, so the room to save arrives in the URL and is handed over to storage here.
  const storeRoomIdFromUrl = () => {
    const url = new URL(window.location.href);
    const roomId = url.searchParams.get(WISHLIST_ROOM_ID_QUERY_PARAM);

    if (roomId === null) {
      return;
    }

    if (/^\d+$/.test(roomId)) {
      window.sessionStorage.setItem(WISHLIST_LOCAL_STORAGE_KEY, roomId);
    }

    url.searchParams.delete(WISHLIST_ROOM_ID_QUERY_PARAM);
    window.history.replaceState(window.history.state, '', url);
  };

  // auto attach events to room card hearts
  const init = (items = [], user) => {
    wishListRooms = items;
    userData = user;

    storeRoomIdFromUrl();
    updateDOMWithState();

    setTimeout(() => {
      updateRoomsFromUrl();
    }, 750);

    $(document).on('click', HEARTS_SELECTOR, (event) => {
      const id = +event.target.dataset.id;

      const tracking = {};
      if (event.target.dataset.trackSection) {
        tracking.section = event.target.dataset.trackSection;
      }
      if (event.target.dataset.trackElement) {
        tracking.element = event.target.dataset.trackElement;
      }

      const saved = toggleSaved(id, tracking);

      if (saved) {
        updateDOMWithState();
      }
    });
  };

  const deferred = jQuery.Deferred();

  $(document).ready(() => {
    App.es6.shared.user_data.getUser().then(user => {
      init(user.wishList, user);

      deferred.resolve({
        TRACKING_ACTIONS,
        getSavedRoomsIds,
        getSavedRoomsCount,
        toggleSaved,
        updateDOMWithState,
        isFavourite,
      });
    });
  });

  return deferred;
});
